import React from 'react';
import { Box, Skeleton, Container, Grid } from '@mui/material';

const ReviewDataSkeleton = () => {
  return (
    <Container disableGutters maxWidth="100%" sx={{ marginTop: 2 }}>
      <Grid container spacing={2} alignItems="center">
        {/* Header Section with Review Data and Next Button */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              marginBottom: 2,
            }}
          >
            {/* Left side - Review Data header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Skeleton
                variant="text"
                width={120}
                height={32}
                sx={{ fontSize: "20px" }}
              />
              {/* Preferences Switch */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Skeleton
                  variant="rectangular"
                  width={40}
                  height={20}
                  sx={{ borderRadius: 10 }}
                />
                <Skeleton variant="text" width={80} height={20} />
              </Box>
            </Box>

            {/* Right side - Next Button */}
            <Skeleton
              variant="rectangular"
              width={150}
              height={40}
              sx={{ borderRadius: 1 }}
            />
          </Box>
        </Grid>

        {/* Main Content Area - Horizontal Scroll */}
        <Grid item xs={12}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexWrap: "nowrap",
              overflowX: "auto",
              gap: 1,
            }}
          >
            {/* Fund Strategy Card */}
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: "8px",
                padding: "10px",
                display: "flex",
                flexDirection: "column",
                gap: 1,
                width: "500px",
                position: "relative",
                minWidth: "500px",
              }}
            >
              {/* Fund Strategy Name with Fullscreen Icon */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 1,
                }}
              >
                <Skeleton
                  variant="text"
                  width={300}
                  height={28}
                  sx={{ fontSize: "18px" }}
                />
                <Skeleton
                  variant="circular"
                  width={20}
                  height={20}
                />
              </Box>

              {/* Summary Table */}
              <Box
                sx={{
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  marginBottom: 2,
                  backgroundColor: "transparent",
                }}
              >
                {/* Table Header */}
                <Box
                  sx={{
                    display: "flex",
                    backgroundColor: "transparent",
                    padding: "8px 12px",
                    borderBottom: "1px solid #ccc",
                  }}
                >
                  <Skeleton variant="text" width={60} height={20} sx={{ mr: 2 }} />
                  <Skeleton variant="text" width={80} height={20} sx={{ mr: 2 }} />
                  <Skeleton variant="text" width={60} height={20} />
                </Box>

                {/* Table Rows */}
                {Array.from({ length: 4 }).map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      padding: "4px 12px",
                      borderBottom: index < 3 ? "1px solid #ccc" : "none",
                      backgroundColor: "transparent",
                    }}
                  >
                    <Skeleton variant="text" width={60} height={16} sx={{ mr: 2 }} />
                    <Skeleton variant="text" width={80} height={16} sx={{ mr: 2 }} />
                    <Skeleton variant="text" width={60} height={16} />
                  </Box>
                ))}
              </Box>

              {/* Drift Alert Box */}
              <Box
                sx={{
                  backgroundColor: "transparent",
                  border: "1px solid #ccc",
                  borderRadius: 2,
                  padding: 2,
                  marginBottom: 2,
                  minHeight: 80,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Skeleton
                    variant="circular"
                    width={16}
                    height={16}
                    sx={{ mr: 1 }}
                  />
                  <Skeleton variant="text" width={150} height={20} />
                </Box>
                <Skeleton variant="text" width={200} height={16} sx={{ mb: 1 }} />
                <Skeleton variant="text" width={180} height={16} sx={{ mb: 2 }} />
                
                {/* Switch to Full Rebalance Button */}
                <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                  <Skeleton
                    variant="rectangular"
                    width={200}
                    height={36}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>
                
                {/* Unchanged Positions Link */}
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Skeleton variant="text" width={120} height={16} />
                </Box>
              </Box>

              {/* Position Legend */}
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  alignItems: "center",
                  marginBottom: 2,
                }}
              >
                {/* NEW POSITION */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Skeleton
                    variant="rectangular"
                    width={20}
                    height={20}
                  />
                  <Skeleton variant="text" width={120} height={20} />
                </Box>

                {/* CLOSING POSITION */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Skeleton
                    variant="rectangular"
                    width={20}
                    height={20}
                  />
                  <Skeleton variant="text" width={140} height={20} />
                </Box>
              </Box>

              {/* Detailed Positions Table */}
              <Box
                sx={{
                 
                  borderRadius: "4px",
                  height: "450px",
                  overflow: "hidden",
                  backgroundColor: "transparent",
                  border: "1px solid #ccc",
                }}
              >
                {/* Table Header */}
                <Box
                  sx={{
                    display: "flex",
                    backgroundColor: "transparent",
                    padding: "8px 5px",
                    borderBottom: "1px solid #ccc",
                  }}
                >
                  <Skeleton variant="text" width={60} height={20} sx={{ mr: 1 }} />
                  <Skeleton variant="text" width={80} height={20} sx={{ mr: 1 }} />
                  <Skeleton variant="text" width={60} height={20} sx={{ mr: 1 }} />
                  <Skeleton variant="text" width={60} height={20} sx={{ mr: 1 }} />
                  <Skeleton variant="text" width={60} height={20} sx={{ mr: 1 }} />
                  <Skeleton variant="text" width={80} height={20} />
                </Box>

                {/* Table Rows */}
                <Box sx={{ height: "400px", overflowY: "auto" }}>
                  {Array.from({ length: 15 }).map((_, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        padding: "4px 5px",
                        borderBottom: index < 14 ? "1px solid #ccc" : "none",
                        backgroundColor: "transparent",
                      }}
                    >
                      <Skeleton variant="text" width={60} height={16} sx={{ mr: 1 }} />
                      <Skeleton variant="text" width={80} height={16} sx={{ mr: 1 }} />
                      <Skeleton variant="text" width={60} height={16} sx={{ mr: 1 }} />
                      <Skeleton variant="text" width={60} height={16} sx={{ mr: 1 }} />
                      <Skeleton variant="text" width={60} height={16} sx={{ mr: 1 }} />
                      <Skeleton variant="text" width={80} height={16} />
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ReviewDataSkeleton; 